import { apiClient, type ApiResponse } from './client'

export type MallOrderStatus =
  | 'PAID'
  | 'FULFILLING'
  | 'FULFILLED'
  | 'CANCELLED'

export type MallFulfillmentStatus =
  | 'PREPARING'
  | 'OUTBOUND_REQUESTING'
  | 'OUTBOUND_REQUESTED'
  | 'OUTBOUND_UNCERTAIN'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'CANCELLED'

export interface MallCategory {
  id: number
  name: string
  slug: string
  sortOrder: number
}

export interface MallProductSummary {
  id: number
  name: string
  slug: string
  shortDescription: string | null
  thumbnailUrl: string | null
  category: MallCategory
  minPrice: number
  currency: string
  available: boolean
  featured: boolean
}

export interface MallProductImage {
  id: number
  url: string
  altText: string | null
  sortOrder: number
}

export interface MallProductSku {
  id: number
  skuCode: string
  name: string
  price: number
  currency: string
  stockQuantity: number | null
  available: boolean
}

export interface MallProductDetail extends MallProductSummary {
  description: string | null
  images: MallProductImage[]
  skus: MallProductSku[]
}

export interface MallCatalog {
  categories: MallCategory[]
  products: MallProductSummary[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

export interface MallCartItem {
  id: number
  skuId: number
  productId: number
  productName: string
  skuName: string
  skuCode: string
  thumbnailUrl: string | null
  unitPrice: number
  currency: string
  quantity: number
  lineTotal: number
  stockQuantity: number | null
  available: boolean
}

export interface MallCart {
  items: MallCartItem[]
  subtotal: number
  shippingFee: number
  totalAmount: number
  totalQuantity: number
  currency: string
  updatedAt: string
}

export interface MallAddress {
  id: number
  label: string
  recipientName: string
  recipientPhone: string
  postalCode: string
  addressLine1: string
  addressLine2: string | null
  deliveryMemo: string | null
  isDefault: boolean
}

export interface MallAddressInput {
  label: string
  recipientName: string
  recipientPhone: string
  postalCode: string
  addressLine1: string
  addressLine2: string | null
  deliveryMemo: string | null
  isDefault: boolean
}

export interface MallOrderItem {
  id: number
  productId: number | null
  skuId: number | null
  productName: string
  skuName: string
  skuCode: string
  thumbnailUrl: string | null
  unitPrice: number
  quantity: number
  lineTotal: number
}

export interface MallShippingAddress {
  label: string
  recipientName: string
  recipientPhone: string
  postalCode: string
  addressLine1: string
  addressLine2: string | null
}

export interface MallOrder {
  id: number
  orderNumber: string
  status: MallOrderStatus
  fulfillmentStatus: MallFulfillmentStatus
  items: MallOrderItem[]
  shippingAddress: MallShippingAddress
  subtotal: number
  shippingFee: number
  totalAmount: number
  currency: string
  orderedAt: string
  cancelledAt: string | null
  cancelReason: string | null
  deliveryMemo: string | null
}

export interface MallOrderPage {
  orders: MallOrder[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

export interface MallCheckoutRequest {
  addressId: number
  billingKeyId: number
  checkoutAttemptId: string
  memo?: string
}

export interface MallCheckoutResponse {
  checkoutAttemptId: string
  idempotentReplay: boolean
  order: MallOrder
}

export interface MallCatalogParams {
  query?: string
  categoryId?: number
  page?: number
  size?: number
}

export interface MallOrderParams {
  query?: string
  dateFrom?: string
  dateTo?: string
  page?: number
  size?: number
}

const basePath = '/lc/admin/mall'

export const mallApi = {
  getCatalog: (params: MallCatalogParams) =>
    apiClient.get<ApiResponse<MallCatalog>>(`${basePath}/catalog`, { params }),
  getProduct: (productId: number) =>
    apiClient.get<ApiResponse<MallProductDetail>>(`${basePath}/products/${productId}`),

  getCart: () =>
    apiClient.get<ApiResponse<MallCart>>(`${basePath}/cart`),
  addCartItem: (body: { skuId: number; quantity: number }) =>
    apiClient.post<ApiResponse<MallCart>>(`${basePath}/cart/items`, body),
  updateCartItem: (itemId: number, body: { quantity: number }) =>
    apiClient.patch<ApiResponse<MallCart>>(`${basePath}/cart/items/${itemId}`, body),
  deleteCartItem: (itemId: number) =>
    apiClient.delete<ApiResponse<MallCart>>(`${basePath}/cart/items/${itemId}`),
  clearCart: () =>
    apiClient.delete<ApiResponse<MallCart>>(`${basePath}/cart`),

  getAddresses: () =>
    apiClient.get<ApiResponse<MallAddress[]>>(`${basePath}/addresses`),
  createAddress: (body: MallAddressInput) =>
    apiClient.post<ApiResponse<MallAddress>>(`${basePath}/addresses`, body),
  updateAddress: (addressId: number, body: MallAddressInput) =>
    apiClient.put<ApiResponse<MallAddress>>(`${basePath}/addresses/${addressId}`, body),
  deleteAddress: (addressId: number) =>
    apiClient.delete<ApiResponse<void>>(`${basePath}/addresses/${addressId}`),
  setDefaultAddress: (addressId: number) =>
    apiClient.post<ApiResponse<MallAddress>>(`${basePath}/addresses/${addressId}/default`),

  checkout: (body: MallCheckoutRequest) =>
    apiClient.post<ApiResponse<MallCheckoutResponse>>(`${basePath}/checkout`, body),
  getOrders: (params: MallOrderParams = {}) =>
    apiClient.get<ApiResponse<MallOrderPage>>(`${basePath}/orders`, { params }),
  getOrder: (orderId: number) =>
    apiClient.get<ApiResponse<MallOrder>>(`${basePath}/orders/${orderId}`),
  cancelOrder: (orderId: number, body: { reason: string }) =>
    apiClient.post<ApiResponse<MallOrder>>(`${basePath}/orders/${orderId}/cancel`, body),
}
